// The file format behind the form builder's Export and Import buttons
// (z8nrz7ep1z), and every rule about what may cross that boundary. Pure — no
// React/network imports — so it is unit-testable like `src/lib/formCopy.ts`
// (vitest only collects `src/**/*.test.ts`, so logic left in the page would
// never be tested).
//
// **The file carries SECTION DEFINITIONS, not the builder's `FormSection`
// shape.** `toSectionDefinitions` already normalizes builder state into exactly
// the shape `buildFormCopyPayload` consumes, so writing that canonical form is
// what lets the round trip reuse the Copy Form path instead of needing a third
// conversion layer. It is also the shape the backend persists, so a file stays
// readable as the builder's internal state drifts.
//
// Everything parsed here is UNTRUSTED input off a user's disk — unlike the
// procedure-template bundle (`src/types/procedureTemplateTransfer.ts`), which
// the server builds and the frontend passes back opaquely. So this module
// validates structurally and then hands a narrowed value on; the import path
// must never spread a parsed object into a save payload (see `formCopy.ts`'s
// header for what the backend's DTO whitelist does with unknown properties).

import type {
  CgtFieldType,
  CgtFormType,
  FieldDefinition,
  FormCategory,
  FormFieldLayout,
  FormulaGate,
  OosConfig,
  SectionDefinition,
  VisibilityRule,
} from '@/types/cgt'
import { FORM_CATEGORY_LABELS } from '@/types/cgt'
import { parseCrossFormRef } from '@/lib/crossForm'
import type { FormCopySource } from '@/lib/formCopy'

// ─── The envelope ────────────────────────────────────────────────────────────

/** Marks a file as one of ours. Checked before anything else is believed. */
export const FORM_EXPORT_KIND = 'cgt.form'

/**
 * The version this build WRITES. Bumped when the file gains something an older
 * reader would misread or drop; an unknown version is refused rather than
 * guessed at — a partially-understood form would import as a quietly wrong
 * form.
 *
 * 2 added `optionSets` (z8nrz7f3qt): a dropdown's `optionSetId` alone means
 * nothing in another tenant, so the file carries each set's name and values.
 */
export const FORM_EXPORT_SCHEMA_VERSION = 2

/**
 * Every version `parseFormExport` reads. A version-1 file is still accepted:
 * it is fine wherever its option sets already exist, and the import preview
 * refuses it, by name, where they do not.
 */
const READABLE_SCHEMA_VERSIONS: ReadonlySet<unknown> = new Set([1, 2])

/**
 * The form as it travels. Mirrors the persisted `FormDefinition` minus
 * everything that identifies the ROW rather than describes the form: no `id`,
 * `version`, `familyId`, `versionName`, `createdAt/By`, `isTestData`.
 *
 * Two deliberate omissions beyond those:
 *
 * - **`handle`** is the permanent cross-form reference namespace and the
 *   backend rejects a change to it, so a copy must get a fresh one. Omitted so
 *   the server derives it from the name, exactly as any other new-form save
 *   does.
 * - **`crossFormTemplateId`** points at a Procedure Template row in the source
 *   deployment. An imported form starts detached from any template — the same
 *   choice the save-as-new-version flow already makes — and the importer says
 *   so with a note rather than carrying a dangling id.
 *
 * The builder's `description` is also absent, because it is never persisted:
 * `FormDefinition` has no such property and the editor resets it to `''` on
 * load. The old export wrote it, which meant writing a value no importer could
 * ever restore.
 *
 * `requiresPatientHistory` is likewise absent, for a different reason: no save
 * path in this app has ever SENT it (it is only ever read off a loaded
 * definition), so there is no evidence `CreateFormDefinitionDto` whitelists it
 * — and the global ValidationPipe 400s on any property it does not. Carrying
 * it would risk failing every import to restore a flag the builder cannot even
 * edit.
 */
export interface FormExportBody {
  name: string
  category: FormCategory
  /** Legacy workflow stage. Still persisted, so still carried. */
  formType?: CgtFormType
  allowedActions?: string[]
  defaultRepeatCount?: number | null
  completionGate?: FormulaGate
  completerGroupIds?: string[]
  /** Form-wide label default (z8nrz7djyx). */
  fieldLayout?: FormFieldLayout | null
  predecessorFormTypes?: CgtFormType[]
  sections: SectionDefinition[]
}

/**
 * An option set a dropdown in the file is bound to, carried by value
 * (z8nrz7f3qt). `id` is the SOURCE tenant's id — the key the form's fields
 * name in `optionSetId`, and nothing more: the import binds each field to a
 * set in the target by name and values, never by this id.
 */
export interface FormExportOptionSet {
  id: string
  name: string
  /** The option labels, in order. */
  values: string[]
}

export interface FormExportFile {
  kind: typeof FORM_EXPORT_KIND
  schemaVersion: number
  /** Provenance for a human reading the file. Never read back on import. */
  exportedAt: string
  form: FormExportBody
  /** One entry per set, however many fields use it. Absent in version-1 files. */
  optionSets?: FormExportOptionSet[]
}

/**
 * Wraps a form body in the envelope. `now` is injected so tests are stable.
 *
 * `optionSets` should hold every set the form's dropdowns reference
 * (`referencedOptionSetIds`); one the caller could not load is simply absent,
 * and the import treats it as a version-1 reference.
 */
export function buildFormExportFile(
  form: FormExportBody,
  now = new Date(),
  optionSets: readonly FormExportOptionSet[] = [],
): FormExportFile {
  return {
    kind: FORM_EXPORT_KIND,
    schemaVersion: FORM_EXPORT_SCHEMA_VERSION,
    exportedAt: now.toISOString(),
    form,
    optionSets: optionSets.map(s => ({ id: s.id, name: s.name, values: [...s.values] })),
  }
}

/** The distinct option-set ids the form's dropdowns are bound to, in form order. */
export function referencedOptionSetIds(form: Pick<FormExportBody, 'sections'>): string[] {
  const ids = new Set<string>()
  for (const section of form.sections) {
    for (const field of section.fields) {
      if (field.optionSetId) ids.add(field.optionSetId)
    }
  }
  return [...ids]
}

/** Unchanged from the original export (AC2): slugified name, `.json`. */
export function formExportFileName(name: string): string {
  return `${(name || 'form').replace(/\s+/g, '-').toLowerCase()}.json`
}

// ─── Refusals and advisories ─────────────────────────────────────────────────

export type FormImportBlockerKind =
  | 'not-json'
  | 'not-a-form-export'
  | 'legacy-export'
  | 'procedure-template-bundle'
  | 'unsupported-schema-version'
  | 'malformed-form'
  | 'no-sections'
  | 'malformed-section'
  | 'malformed-field'

/** Something that makes the file unreadable or the import wrong. Any one refuses it. */
export interface FormImportBlocker {
  kind: FormImportBlockerKind
  message: string
}

export type FormImportNoteKind =
  | 'library-field-unlinked'
  | 'library-field-relinked'
  | 'library-system-name-missing'
  | 'signer-group-cleared'
  | 'completer-group-cleared'
  | 'alert-group-cleared'
  | 'cross-form-ref-kept'
  | 'cross-form-ref-missing-form'
  | 'template-link-dropped'

/**
 * A decision the import made that the user must see but that stops nothing.
 * Same distinction the procedure-template import draws: a note means the
 * result is right, but is not the naive reading of "import everything".
 */
export interface FormImportNote {
  kind: FormImportNoteKind
  message: string
}

// ─── Runtime type guards ─────────────────────────────────────────────────────
// `toBuilderFieldType` / `toCgtFieldType` are exhaustive switches with NO
// default, which is right for compile-time safety but means an unrecognized
// `fieldType` string off a disk falls through and returns `undefined`. Nothing
// downstream checks for that, so the field would render as nothing at all.
// Validating against a full-union table closes the hole, and because the table
// is a `Record` over the whole union, adding a member to `CgtFieldType` stops
// this file compiling — which is where that mistake belongs.
//
// Categories reuse `FORM_CATEGORY_LABELS` from the domain dictionary rather
// than keeping a second list in step with it. Field types get a local table
// because the only existing one (`FIELD_TYPE_LABELS`) lives in
// `src/pages/FieldLibraryPage.tsx`, and a pure lib module importing from a page
// would drag that page's import graph into everything downstream.

const CGT_FIELD_TYPES: Record<CgtFieldType, true> = {
  text: true,
  number: true,
  date: true,
  datetime: true,
  textarea: true,
  checkbox: true,
  dropdown: true,
  calculated: true,
  signature: true,
  process: true,
  section: true,
  table: true,
  image: true,
  attachment: true,
  description: true,
  review: true,
}

function has(table: Record<string, unknown>, v: unknown): boolean {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(table, v)
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== ''
}

// ─── Parse ───────────────────────────────────────────────────────────────────

export type FormImportParse =
  | { ok: false; blockers: FormImportBlocker[] }
  | {
      ok: true
      form: FormExportBody
      /** The file's option sets; empty for a version-1 file. */
      optionSets: FormExportOptionSet[]
      sectionCount: number
      fieldCount: number
    }

/**
 * Reads a file's text into a `FormExportBody`, or refuses it with reasons.
 *
 * Validation is structural, not exhaustive: it proves the shape the builder and
 * `buildFormCopyPayload` walk is present and typed (sections → fields → id,
 * label, fieldType), and leaves leaf props to the backend DTO, which validates
 * them anyway. The aim is to refuse a file that would import as a BROKEN form,
 * not to re-implement the server's validator client-side.
 *
 * Returns ALL blockers it finds rather than the first, so a user fixing a
 * hand-edited file sees the whole list in one pass.
 */
export function parseFormExport(text: string): FormImportParse {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return {
      ok: false,
      blockers: [{ kind: 'not-json', message: 'This file is not valid JSON.' }],
    }
  }

  if (!isRecord(raw)) {
    return {
      ok: false,
      blockers: [{ kind: 'not-a-form-export', message: 'This file does not contain a form.' }],
    }
  }

  // Named before the generic marker check so the far more likely mistake —
  // picking a procedure-template bundle, the other downloadable JSON in this
  // app — gets a message that says which file was chosen.
  if (isRecord(raw.bundle) || Array.isArray(raw.forms)) {
    return {
      ok: false,
      blockers: [
        {
          kind: 'procedure-template-bundle',
          message:
            'This is a procedure-template bundle, not a form. Import it from Procedure Templates instead.',
        },
      ],
    }
  }

  // The builder's Export before z8nrz7ep1z wrote the builder's own state with
  // no envelope at all: `{ name, description, sections }`. Recognised so the
  // user is told to re-export rather than that the file is not a form, which
  // it plainly is (z8nrz7e698 AC4.2).
  if (raw.kind === undefined && Array.isArray(raw.sections)) {
    return {
      ok: false,
      blockers: [
        {
          kind: 'legacy-export',
          message:
            'This file was exported by an older or unsupported version. Re-export the form and try again.',
        },
      ],
    }
  }

  if (raw.kind !== FORM_EXPORT_KIND) {
    return {
      ok: false,
      blockers: [
        {
          kind: 'not-a-form-export',
          message:
            'This file was not exported from the form builder (its "kind" marker is missing or different).',
        },
      ],
    }
  }

  if (!READABLE_SCHEMA_VERSIONS.has(raw.schemaVersion)) {
    return {
      ok: false,
      blockers: [
        {
          kind: 'unsupported-schema-version',
          message: `This file uses form format version ${String(raw.schemaVersion)}, and this app reads version ${FORM_EXPORT_SCHEMA_VERSION}.`,
        },
      ],
    }
  }

  const form = raw.form
  const blockers: FormImportBlocker[] = []

  if (!isRecord(form)) {
    return {
      ok: false,
      blockers: [{ kind: 'malformed-form', message: 'The file has no form in it.' }],
    }
  }
  if (!isNonEmptyString(form.name)) {
    blockers.push({ kind: 'malformed-form', message: 'The form in this file has no name.' })
  }
  if (!has(FORM_CATEGORY_LABELS, form.category)) {
    blockers.push({
      kind: 'malformed-form',
      message: `Unknown form type "${String(form.category)}".`,
    })
  }

  if (!Array.isArray(form.sections) || form.sections.length === 0) {
    blockers.push({ kind: 'no-sections', message: 'The form in this file has no sections.' })
    return { ok: false, blockers }
  }

  let fieldCount = 0
  form.sections.forEach((section, si) => {
    const where = `Section ${si + 1}`
    if (!isRecord(section)) {
      blockers.push({ kind: 'malformed-section', message: `${where} is not a section.` })
      return
    }
    if (!isNonEmptyString(section.id)) {
      blockers.push({ kind: 'malformed-section', message: `${where} has no id.` })
    }
    if (!Array.isArray(section.fields)) {
      blockers.push({ kind: 'malformed-section', message: `${where} has no field list.` })
      return
    }
    section.fields.forEach((field, fi) => {
      const fieldWhere = `${where}, field ${fi + 1}`
      if (!isRecord(field)) {
        blockers.push({ kind: 'malformed-field', message: `${fieldWhere} is not a field.` })
        return
      }
      fieldCount++
      if (!isNonEmptyString(field.id)) {
        blockers.push({ kind: 'malformed-field', message: `${fieldWhere} has no id.` })
      }
      if (!has(CGT_FIELD_TYPES, field.fieldType)) {
        blockers.push({
          kind: 'malformed-field',
          message: `${fieldWhere} has an unknown field type "${String(field.fieldType)}".`,
        })
      }
    })
  })

  const optionSets: FormExportOptionSet[] = []
  if (raw.optionSets !== undefined) {
    if (!Array.isArray(raw.optionSets)) {
      blockers.push({ kind: 'malformed-form', message: 'The option sets in this file are not a list.' })
    } else {
      raw.optionSets.forEach((set, i) => {
        if (
          !isRecord(set) ||
          !isNonEmptyString(set.id) ||
          !isNonEmptyString(set.name) ||
          !Array.isArray(set.values) ||
          !set.values.every(v => typeof v === 'string')
        ) {
          blockers.push({
            kind: 'malformed-form',
            message: `Option set ${i + 1} in this file is missing its id, name or values.`,
          })
          return
        }
        optionSets.push({ id: set.id, name: set.name, values: set.values as string[] })
      })
    }
  }

  if (blockers.length > 0) return { ok: false, blockers }

  // Narrowing is safe only for what was checked above; the rest of the body is
  // read through named properties by `toImportedFormSource`, never spread.
  const body = form as unknown as FormExportBody
  return { ok: true, form: body, optionSets, sectionCount: body.sections.length, fieldCount }
}

// ─── Formula walking ─────────────────────────────────────────────────────────

/**
 * Every formula string anywhere in a form, in one place: calculated fields,
 * unlock and signature gates, visibility conditions (field AND section level),
 * table-cell formulas, and the whole-form completion gate.
 *
 * Exactly the surfaces `formCopy.ts` remaps, which is the point — a token that
 * gets rewritten there must be a token this can see, or the reference scan
 * below would miss it.
 */
function forEachFormula(form: FormExportBody, visit: (formula: string) => void): void {
  const visitRule = (rule: VisibilityRule | undefined) => {
    rule?.groups?.forEach(g =>
      g.conditions?.forEach(c => {
        if (c.formula) visit(c.formula)
      }),
    )
  }

  if (form.completionGate?.formula) visit(form.completionGate.formula)

  for (const section of form.sections) {
    visitRule(section.visibility)
    for (const field of section.fields) {
      if (field.formula) visit(field.formula)
      if (field.unlockGate?.formula) visit(field.unlockGate.formula)
      if (field.signatureGate?.formula) visit(field.signatureGate.formula)
      visitRule(field.visibility)
      for (const cell of Object.values(field.tableConfig?.cells ?? {})) {
        if (cell.formula) visit(cell.formula)
      }
    }
  }
}

/**
 * The `{handle.fieldId}` refs in a form, grouped by the form handle they name.
 *
 * A cross-form ref is never rewritten on import — it addresses ANOTHER form's
 * namespace, so the id it carries is not this form's to re-mint. What can
 * change is whether the form it names is here at all.
 */
function crossFormRefsByHandle(form: FormExportBody): Map<string, string[]> {
  const byHandle = new Map<string, string[]>()
  const seen = new Set<string>()
  forEachFormula(form, formula => {
    for (const match of formula.matchAll(/\{([^}]+)\}/g)) {
      const parsed = parseCrossFormRef(match[1])
      if (!parsed || seen.has(match[1])) continue
      seen.add(match[1])
      const refs = byHandle.get(parsed.handle) ?? []
      refs.push(match[1])
      byHandle.set(parsed.handle, refs)
    }
  })
  return byHandle
}

// ─── Plan ────────────────────────────────────────────────────────────────────

/** What this tenant actually has, for resolving the file's soft references. */
export interface FormImportTenantContext {
  /** Field Library field ids (`libraryFieldId` provenance targets). */
  libraryFieldIds: ReadonlySet<string>
  /**
   * A Field Library field id per curated system name, for relinking a field
   * whose file carries `librarySystemName` but an id from another environment.
   * Optional so callers that predate it keep their behaviour.
   */
  libraryFieldIdBySystemName?: ReadonlyMap<string, string>
  /** User group ids (signature signers, form completers). */
  userGroupIds: ReadonlySet<string>
  /** Names of forms that already exist, for the de-duplicated default name. */
  existingFormNames: readonly string[]
  /**
   * Handles of the forms in this tenant, for deciding whether a cross-form ref
   * points at a form that is actually here.
   *
   * `null` means "could not be established" — the caller could not see the
   * whole catalog. A missing form is then NOT claimed, because naming a form
   * as absent when the list was merely truncated is a worse error than staying
   * vague: the author would go looking for a form that exists.
   */
  existingFormHandles: ReadonlySet<string> | null
}

export interface FormImportPlan {
  /** The proposed name — de-duplicated, and editable before committing. */
  name: string
  /** The form with unresolvable references cleared. What actually gets created. */
  form: FormExportBody
  notes: FormImportNote[]
  sectionCount: number
  fieldCount: number
}

/**
 * The default name for an imported form.
 *
 * Forms are not unique by name, so a collision is allowed — but silently
 * creating a second "Patient Chart" leaves two indistinguishable rows in the
 * catalog, so the proposal is suffixed instead. `(imported)` first, then
 * `(imported 2)`, and so on.
 */
export function importedFormName(sourceName: string, existingNames: readonly string[]): string {
  const taken = new Set(existingNames.map(n => n.trim().toLowerCase()))
  const base = sourceName.trim() || 'Imported form'
  if (!taken.has(base.toLowerCase())) return base

  const first = `${base} (imported)`
  if (!taken.has(first.toLowerCase())) return first
  for (let n = 2; ; n++) {
    const candidate = `${base} (imported ${n})`
    if (!taken.has(candidate.toLowerCase())) return candidate
  }
}

/** `1 form` / `3 forms` — the count and its noun, so messages read naturally. */
function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/** `(a, b, …)` — a short parenthetical sample, capped so a long list stays readable. */
function list(items: string[], max = 3): string {
  return `(${items.slice(0, max).join(', ')}${items.length > max ? ', …' : ''})`
}

function keepKnown(
  ids: string[] | undefined,
  known: ReadonlySet<string>,
): { kept: string[] | undefined; dropped: number } {
  if (!ids || ids.length === 0) return { kept: ids, dropped: 0 }
  const kept = ids.filter(id => known.has(id))
  return { kept, dropped: ids.length - kept.length }
}

/**
 * Decides what importing this file into THIS tenant will produce: the name, the
 * form with unresolvable references stripped, and the notes explaining every
 * strip.
 *
 * Clearing a reference is not tidiness. A `completerGroupIds` or
 * `allowedSignerGroupIds` entry with no group behind it is refused by the
 * server outright (see `UserGroupMultiSelect`), so carrying one through would
 * turn an otherwise-valid import into a failed save with an opaque message. A
 * stale `libraryFieldId` is milder — it is provenance, used by the backend's
 * "used by forms" query — but pointing at a row this tenant does not have makes
 * that query wrong, so it goes too.
 *
 * Returns no blockers: by this point the file parsed, and nothing a tenant
 * merely LACKS is a reason to refuse. The user sees the notes and decides.
 */
export function planFormImport(
  parsed: FormExportBody,
  ctx: FormImportTenantContext,
): FormImportPlan {
  const notes: FormImportNote[] = []

  const completers = keepKnown(parsed.completerGroupIds, ctx.userGroupIds)
  if (completers.dropped > 0) {
    notes.push({
      kind: 'completer-group-cleared',
      message: `${completers.dropped} completer email group${completers.dropped === 1 ? '' : 's'} on this form ${completers.dropped === 1 ? 'does' : 'do'} not exist here and will be cleared — anyone who can complete forms will be able to complete it.`,
    })
  }

  let unlinkedLibraryFields = 0
  let relinkedLibraryFields = 0
  const missingSystemNames = new Set<string>()
  let clearedSignerGroups = 0
  let clearedAlertGroups = 0
  let removedSpecs = 0

  const sections: SectionDefinition[] = parsed.sections.map(section => ({
    ...section,
    fields: section.fields.map((field): FieldDefinition => {
      const signers = keepKnown(field.allowedSignerGroupIds, ctx.userGroupIds)
      if (signers.dropped > 0) clearedSignerGroups++
      const knownId =
        field.libraryFieldId && ctx.libraryFieldIds.has(field.libraryFieldId)
          ? field.libraryFieldId
          : undefined
      // An id from another environment, or none at all: the system name is
      // what both environments agree on, so it decides the link. This is what
      // lets a form whose dose inputs are identified by system name import with
      // those inputs still attached.
      const bySystemName =
        !knownId && field.librarySystemName
          ? ctx.libraryFieldIdBySystemName?.get(field.librarySystemName)
          : undefined
      const libraryFieldId = knownId ?? bySystemName
      if (bySystemName) relinkedLibraryFields++
      else if (!knownId && field.librarySystemName) missingSystemNames.add(field.librarySystemName)
      else if (field.libraryFieldId && !knownId) unlinkedLibraryFields++

      // Table cells carry the same link (a cell that is a library field), resolved
      // by the same rule. The file-only `librarySystemName` is dropped here:
      // cells are copied by spreading, so it would otherwise be saved.
      const tableConfig = field.tableConfig?.cells
        ? {
            ...field.tableConfig,
            cells: Object.fromEntries(
              Object.entries(field.tableConfig.cells).map(([key, cell]) => {
                const { librarySystemName, ...rest } = cell
                const cellKnown =
                  rest.libraryFieldId && ctx.libraryFieldIds.has(rest.libraryFieldId)
                    ? rest.libraryFieldId
                    : undefined
                const cellBySystemName =
                  !cellKnown && librarySystemName
                    ? ctx.libraryFieldIdBySystemName?.get(librarySystemName)
                    : undefined
                if (cellBySystemName) relinkedLibraryFields++
                else if (!cellKnown && librarySystemName) missingSystemNames.add(librarySystemName)
                else if (rest.libraryFieldId && !cellKnown) unlinkedLibraryFields++
                const cellLink = cellKnown ?? cellBySystemName
                return [key, { ...rest, libraryFieldId: cellLink }]
              }),
            ),
          }
        : field.tableConfig

      // A specification alerts groups by id (z8nrz7eq4e). Unknown ones go; a
      // specification left with no group cannot be saved, so it goes too.
      // Table columns and cells carry specifications under the same rule.
      const cleanSpec = (spec: OosConfig | undefined): OosConfig | undefined => {
        if (!spec) return spec
        const alertGroups = keepKnown(spec.alertGroupIds, ctx.userGroupIds)
        if (alertGroups.dropped > 0) clearedAlertGroups++
        if (alertGroups.kept && alertGroups.kept.length > 0) return { ...spec, alertGroupIds: alertGroups.kept }
        removedSpecs++
        return undefined
      }
      const oos = cleanSpec(field.oos)
      const tableSpecs = tableConfig
        ? {
            ...tableConfig,
            columns: tableConfig.columns.map(c => (c.oos ? { ...c, oos: cleanSpec(c.oos) } : c)),
            ...(tableConfig.cells
              ? {
                  cells: Object.fromEntries(
                    Object.entries(tableConfig.cells).map(([key, cell]) => [
                      key,
                      cell.oos ? { ...cell, oos: cleanSpec(cell.oos) } : cell,
                    ]),
                  ),
                }
              : {}),
          }
        : tableConfig
      return { ...field, allowedSignerGroupIds: signers.kept, libraryFieldId, tableConfig: tableSpecs, oos }
    }),
  }))

  if (clearedSignerGroups > 0) {
    notes.push({
      kind: 'signer-group-cleared',
      message: `${clearedSignerGroups} signature field${clearedSignerGroups === 1 ? '' : 's'} name${clearedSignerGroups === 1 ? 's' : ''} an email group that does not exist here; the signer restriction will be cleared, so anyone who can edit the form may sign.`,
    })
  }
  if (clearedAlertGroups > 0) {
    notes.push({
      kind: 'alert-group-cleared',
      message:
        removedSpecs > 0
          ? `${clearedAlertGroups} field${clearedAlertGroups === 1 ? '' : 's'} with a specification alert${clearedAlertGroups === 1 ? 's' : ''} an email group that does not exist here. ${removedSpecs} specification${removedSpecs === 1 ? ' is' : 's are'} left with no group and will be removed; add ${removedSpecs === 1 ? 'it' : 'them'} again in the builder.`
          : `${clearedAlertGroups} field${clearedAlertGroups === 1 ? '' : 's'} with a specification alert${clearedAlertGroups === 1 ? 's' : ''} an email group that does not exist here; that group will be removed from the alert list.`,
    })
  }
  if (relinkedLibraryFields > 0) {
    notes.push({
      kind: 'library-field-relinked',
      message: `${plural(relinkedLibraryFields, 'field')} or table cell${relinkedLibraryFields === 1 ? '' : 's'} linked to the Field Library by system name.`,
    })
  }
  if (missingSystemNames.size > 0) {
    const names = [...missingSystemNames]
    notes.push({
      kind: 'library-system-name-missing',
      message: `No Field Library field here has the system name ${list(names, 10)}. Those fields will be imported without the library link, so anything that reads them by system name (Split and Pool read the lab values this way) will not find them. Import the Field Library entries first, then import this form again.`,
    })
  }
  if (unlinkedLibraryFields > 0) {
    notes.push({
      kind: 'library-field-unlinked',
      message: `${unlinkedLibraryFields} field${unlinkedLibraryFields === 1 ? '' : 's'} came from a Field Library entry that does not exist here. The field${unlinkedLibraryFields === 1 ? '' : 's'} will be imported, but without the library link.`,
    })
  }

  // Cross-form references never block an import. A form whose calculation
  // names a form that is not here is still a form worth having: the author
  // imports it, then creates or imports the referenced form, and the reference
  // starts resolving. Refusing would make the two forms impossible to import
  // in any order.
  const refsByHandle = crossFormRefsByHandle(parsed)
  if (refsByHandle.size > 0) {
    const known = ctx.existingFormHandles
    const missingHandles = known ? [...refsByHandle.keys()].filter(h => !known.has(h)) : []
    const presentHandles = [...refsByHandle.keys()].filter(h => !missingHandles.includes(h))
    const refsOf = (handles: string[]) => handles.flatMap(h => refsByHandle.get(h) ?? [])

    if (missingHandles.length > 0) {
      const refs = refsOf(missingHandles)
      notes.push({
        kind: 'cross-form-ref-missing-form',
        message:
          `${plural(missingHandles.length, 'form')} this one calculates from ${missingHandles.length === 1 ? 'is' : 'are'} not in this tenant: ` +
          `${missingHandles.join(', ')}. The form is still imported and ${plural(refs.length, 'reference')} to ${missingHandles.length === 1 ? 'it' : 'them'} ` +
          `${list(refs)} stay${refs.length === 1 ? 's' : ''} as written — calculations using ${refs.length === 1 ? 'it' : 'them'} will not compute until ` +
          `${missingHandles.length === 1 ? 'that form exists' : 'those forms exist'} here.`,
      })
    }

    if (presentHandles.length > 0) {
      const refs = refsOf(presentHandles)
      const source = known
        ? `${plural(presentHandles.length, 'other form')} that ${presentHandles.length === 1 ? 'is' : 'are'} already here`
        : plural(presentHandles.length, 'other form')
      notes.push({
        kind: 'cross-form-ref-kept',
        message:
          `This form reads ${plural(refs.length, 'field')} from ${source}. Those references are kept as ` +
          'written and resolve once the new form is added to a Procedure Template alongside them.',
      })
    }

    notes.push({
      kind: 'template-link-dropped',
      message:
        'The imported form starts detached from any Procedure Template, the same as a new form version does.',
    })
  }

  return {
    name: importedFormName(parsed.name, ctx.existingFormNames),
    form: { ...parsed, completerGroupIds: completers.kept, sections },
    notes,
    sectionCount: sections.length,
    fieldCount: sections.reduce((n, s) => n + s.fields.length, 0),
  }
}

/**
 * The planned form as a source `buildFormCopyPayload` can consume.
 *
 * Import deliberately goes through the Copy Form path rather than saving the
 * file's own ids: `ProcessStepConnection` is unique on `(tenantId, fieldId)`,
 * so a preserved field id would make the new form silently share the SOURCE
 * form's process configuration. Re-minting is a correctness requirement, and
 * `buildFormCopyPayload` is the one implementation of it (it also rewrites
 * every `{token}`, including table `rowId__colId` composites, which a
 * hand-rolled remap would miss).
 */
export function toImportedFormSource(form: FormExportBody): FormCopySource {
  return {
    name: form.name,
    category: form.category,
    formType: form.formType,
    sections: form.sections,
    predecessorFormTypes: form.predecessorFormTypes,
    allowedActions: form.allowedActions,
    completionGate: form.completionGate,
    completerGroupIds: form.completerGroupIds,
    fieldLayout: form.fieldLayout,
  }
}
